import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/LanguageContext";
import t from "@/translations";

// Flag SVG components
function FlagDE() {
  return (
    <svg className="w-6 h-4 rounded-sm" viewBox="0 0 24 16" xmlns="http://www.w3.org/2000/svg">
      <rect width="24" height="5.33" fill="#000" />
      <rect y="5.33" width="24" height="5.33" fill="#DD0000" />
      <rect y="10.66" width="24" height="5.34" fill="#FFCE00" />
    </svg>
  );
}

function FlagGB() {
  return (
    <svg className="w-6 h-4 rounded-sm" viewBox="0 0 24 16" xmlns="http://www.w3.org/2000/svg">
      <rect width="24" height="16" fill="#012169" />
      <path d="M0,0 L24,16 M24,0 L0,16" stroke="#fff" strokeWidth="2.5" />
      <path d="M0,0 L24,16 M24,0 L0,16" stroke="#C8102E" strokeWidth="1.5" />
      <path d="M12,0 V16 M0,8 H24" stroke="#fff" strokeWidth="4" />
      <path d="M12,0 V16 M0,8 H24" stroke="#C8102E" strokeWidth="2.5" />
    </svg>
  );
}

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { lang, toggle } = useLang();
  const tx = t[lang];

  const navLinks = [
    { label: tx.nav_argumente, href: "#argumente" },
    { label: tx.nav_veranstaltungen, href: "#veranstaltungen" },
    { label: tx.nav_kontakt, href: "#kontakt" },
  ];

  const scrollTo = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="header"
      className="fixed top-0 left-0 right-0 z-50 bg-[#0C1620]/80 backdrop-blur-xl border-b border-[#3A4F60]/30"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14 sm:h-16">
        <Link to="/" className="flex items-center gap-2 sm:gap-3 flex-shrink-0" data-testid="logo-link">
          <img src="/images/vmbs-logo.png" alt="VMBS UG Logo" className="h-8 w-8 sm:h-10 sm:w-10 object-contain" data-testid="vmbs-logo" />
          <span className="font-heading text-lg sm:text-xl tracking-widest text-white font-semibold hidden sm:inline">
            V<span className="text-[#C5993A]">·</span>M<span className="text-[#C5993A]">·</span>B<span className="text-[#C5993A]">·</span>S
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-7">
          {navLinks.map((link) => (
            <button
              key={link.href}
              onClick={() => scrollTo(link.href)}
              className="text-xs xl:text-sm uppercase tracking-[0.12em] text-[#8899AA] hover:text-[#C5993A] transition-colors duration-300"
            >
              {link.label}
            </button>
          ))}

          {/* Language toggle - show opposite flag */}
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            className="p-1.5 rounded hover:bg-[#3A4F60]/30 transition-colors"
            title={lang === "de" ? "Switch to English" : "Auf Deutsch wechseln"}
          >
            {lang === "de" ? <FlagGB /> : <FlagDE />}
          </button>

          <Button
            data-testid="header-cta-button"
            onClick={() => scrollTo("#kontakt")}
            className="bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white font-medium px-4 xl:px-5 py-2 text-sm rounded hover:from-[#8B6B20] hover:to-[#C5993A] transition-all border-0"
          >
            {tx.header_cta}
          </Button>
        </nav>

        <div className="flex items-center gap-2 lg:hidden">
          {/* Mobile language toggle */}
          <button
            data-testid="lang-toggle-mobile"
            onClick={toggle}
            className="p-1.5 rounded hover:bg-[#3A4F60]/30 transition-colors"
          >
            {lang === "de" ? <FlagGB /> : <FlagDE />}
          </button>
          <button
            data-testid="mobile-menu-toggle"
            className="text-[#F0F4F8] p-1"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div data-testid="mobile-menu" className="lg:hidden bg-[#12202f] border-t border-[#3A4F60]/30 px-4 sm:px-6 py-4 space-y-3">
          {navLinks.map((link) => (
            <button
              key={link.href}
              onClick={() => scrollTo(link.href)}
              className="block w-full text-left text-sm uppercase tracking-[0.12em] text-[#8899AA] hover:text-[#C5993A] transition-colors py-2"
            >
              {link.label}
            </button>
          ))}
          <Button onClick={() => scrollTo("#kontakt")} className="w-full bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white border-0 text-sm">
            {tx.header_cta}
          </Button>
        </div>
      )}
    </header>
  );
}
